import * as THREE from 'three';
import type { BattleState, Enemy } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { nameSprite } from './crowd.ts';
import { GlowLayer } from './glow.ts';

/*
 * The Prestacaras (Caras prestadas 4, docs/SAGAS.md): the lanterns standing on the field (under one, it loses its
 * face), the strips of paper that fly off when it does or when it changes face, and over its head the name of the
 * friend whose face it wears. Two merged meshes (posts, lantern shades), one instanced mesh of strips, one glow layer,
 * one name sprite; no lights.
 */

const K = WORLD_PER_HEX / 1000;
const POOL = new THREE.Color('#ffb070');
const POOL_HOT = new THREE.Color('#ff6a4a');
const FLAME = new THREE.Color('#ffd9a8');
const PALE = new THREE.Color('#dfe6f4');

interface Paper {
  p: THREE.Vector3;
  v: THREE.Vector3;
  r: THREE.Euler;
  w: THREE.Vector3;
  life: number;
  max: number;
}

export class FacetakerLayer {
  readonly group = new THREE.Group();
  private glow: GlowLayer;
  private posts: THREE.Mesh[] = [];
  private lamps: THREE.Vector3[] = [];
  private papers: THREE.InstancedMesh;
  private paper: Paper[] = [];
  private tag: THREE.Sprite | null = null;
  private tagText = '';
  private now = 0;
  private last = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private s3 = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    lanterns: { at: { x: number; y: number }[]; r: number } | undefined,
    private radius = (lanterns?.r ?? 1300) * K,
  ) {
    this.glow = new GlowLayer(ctx.low ? 96 : 192);
    this.group.add(this.glow.mesh);
    const pg = new THREE.PlaneGeometry(0.12, 0.26);
    this.papers = new THREE.InstancedMesh(pg, new THREE.MeshBasicMaterial({ color: '#eef0f3', side: THREE.DoubleSide }), 128);
    this.papers.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.papers.count = 0;
    this.papers.frustumCulled = false;
    this.group.add(this.papers);
    this.buildLanterns(lanterns?.at ?? []);
  }

  /** Tall iron posts with a big lantern each, a paper shade in an iron cage, a pale ribbon tied to the post. */
  private buildLanterns(at: { x: number; y: number }[]): void {
    const iron = new Kit();
    const shade = new Kit();
    for (const l of at) {
      const p = this.ctx.at(l.x, l.y, 0);
      const yaw = Math.atan2(p.x, p.z);
      iron.add(new THREE.CylinderGeometry(0.05, 0.08, 2.9, 6).translate(0, 1.45, 0), '#2c2a2a', place(p.x, p.y, p.z));
      iron.add(new THREE.CylinderGeometry(0.2, 0.26, 0.12, 6).translate(0, 0.06, 0), '#3a3634', place(p.x, p.y, p.z));
      // An arm out from the top, the lantern hanging from it.
      iron.add(new THREE.BoxGeometry(0.06, 0.06, 0.55).translate(0, 2.85, 0.24), '#2c2a2a', place(p.x, p.y, p.z, yaw));
      const hang = new THREE.Vector3(Math.sin(yaw) * 0.48, 2.42, Math.cos(yaw) * 0.48).add(p);
      iron.add(new THREE.CylinderGeometry(0.012, 0.012, 0.36, 4).translate(0, 0.25, 0), '#2c2a2a', place(hang.x, hang.y, hang.z));
      // The cage: a roof cap, a floor and four bars round a tall paper shade.
      iron.add(new THREE.ConeGeometry(0.26, 0.18, 4).translate(0, 0.32, 0), '#2c2a2a', place(hang.x, hang.y, hang.z, Math.PI / 4));
      iron.add(new THREE.CylinderGeometry(0.2, 0.2, 0.04, 4).translate(0, -0.28, 0), '#2c2a2a', place(hang.x, hang.y, hang.z, Math.PI / 4));
      for (let i = 0; i < 4; i++) {
        const a = Math.PI / 4 + (i * Math.PI) / 2;
        iron.add(new THREE.BoxGeometry(0.025, 0.58, 0.025), '#2c2a2a', place(hang.x + Math.cos(a) * 0.17, hang.y, hang.z + Math.sin(a) * 0.17));
      }
      shade.add(new THREE.CylinderGeometry(0.15, 0.15, 0.5, 8), '#ffe2bc', place(hang.x, hang.y, hang.z));
      // A strip of pale paper tied to the post, as if torn off a face.
      shade.add(new THREE.PlaneGeometry(0.08, 0.36).translate(0, -0.18, 0), '#e8ecf4', place(p.x + Math.sin(yaw) * 0.06, p.y + 1.7, p.z + Math.cos(yaw) * 0.06, yaw, 1, 0.15, 0.2));
      this.lamps.push(hang);
    }
    const im = iron.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.4 }), 'facetaker-posts');
    const sm = shade.build(new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }), 'facetaker-shades');
    for (const m of [im, sm]) if (m) {
      this.group.add(m);
      this.posts.push(m);
    }
  }

  /** Strips of paper bursting off at a world point (its face slipping, or changing). */
  private strips(p: THREE.Vector3, n: number, spread: number, swirl = 0): void {
    const count = this.ctx.low ? Math.ceil(n / 2) : n;
    for (let i = 0; i < count && this.paper.length < 128; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (0.025 + Math.random() * 0.04) * spread;
      this.paper.push({
        p: p.clone().add(new THREE.Vector3((Math.random() - 0.5) * 0.3, (Math.random() - 0.3) * 0.5, (Math.random() - 0.5) * 0.3)),
        v: new THREE.Vector3(Math.cos(a) * sp - Math.sin(a) * swirl, 0.025 + Math.random() * 0.05, Math.sin(a) * sp + Math.cos(a) * swirl),
        r: new THREE.Euler(Math.random() * 3, Math.random() * 3, Math.random() * 3),
        w: new THREE.Vector3((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3),
        life: 0,
        max: 45 + Math.random() * 35,
      });
    }
  }

  /** Under a lantern its face slips: a big burst of wet paper and a cold flash. */
  unmask(x: number, y: number): void {
    const p = this.ctx.at(x, y, 1.8);
    this.ctx.flare(p, '#e8eeff', 5, 16);
    this.ctx.vfx.ring(this.ctx.at(x, y, 0.06), 0.3, 2.4, '#e8ecf4', 14, 0.75);
    this.ctx.shake(0.25);
    this.strips(p, 44, 1.4);
  }

  /** It takes another face: a swirl of paper round it. */
  face(x: number, y: number): void {
    const p = this.ctx.at(x, y, 1.4);
    this.ctx.flare(p, '#c9d4ff', 3, 12);
    this.ctx.vfx.ring(this.ctx.at(x, y, 0.06), 0.4, 1.6, '#c9d4ff', 12, 0.6);
    this.strips(p, 30, 0.6, 0.05);
  }

  /** Every redraw: the lanterns' light, and over its head whose face it wears (or nothing, bare). */
  draw(s: BattleState, e: Enemy | undefined, p: THREE.Vector3 | undefined, top: number): void {
    this.glow.begin();
    const t = this.now;
    for (let i = 0; i < this.lamps.length; i++) {
      const l = this.lamps[i] as THREE.Vector3;
      const f = 0.9 + 0.1 * Math.sin(t * 5 + i * 1.3);
      // Its pool on the ground, the reach it bares the face in, the flame in the shade.
      const near = !!(e && p && e.mask && e.mask.bare === 0 && e.mask.cd === 0 && Math.hypot(p.x - l.x, p.z - l.z) < this.radius * 1.6);
      this.glow.add(l.x, this.ctx.ground(l.x, l.z) + 0.06, l.z, near ? POOL_HOT : POOL, this.radius * 2, (near ? 0.22 : 0.15) * f);
      this.glow.add(l.x, l.y, l.z, FLAME, 1.2, 0.9 * f);
      this.glow.add(l.x, l.y, l.z, POOL, 3.2, 0.25 * f);
    }
    const m = e?.mask;
    const face = m && m.bare === 0 ? (m.faces.length ? m.faces[m.at % m.faces.length] : null) : undefined;
    const text = face === undefined ? '' : face ? `«${face.name}»` : '«Cal»';
    if (text !== this.tagText) {
      if (this.tag) {
        this.tag.material.map?.dispose();
        this.tag.material.dispose();
        this.tag.removeFromParent();
        this.tag = null;
      }
      if (text) {
        this.tag = nameSprite(text);
        this.tag.material.color.set('#dfe6f4');
        // Bigger than a hero's tag: whose face it is, is the moment.
        this.tag.scale.multiplyScalar(1.7);
        this.group.add(this.tag);
      }
      this.tagText = text;
    }
    if (this.tag && p) this.tag.position.set(p.x, p.y + top + 0.45, p.z);
    if (e && p && m) {
      // A cold shimmer at its edges while it wears a face; bare, pale strips keep peeling off it.
      if (m.bare === 0) {
        // A faint cold edge, and now and then a glint where the borrowed face doesn't quite fit.
        this.glow.add(p.x, p.y + top * 0.5, p.z, PALE, top * 0.75, 0.05 + 0.03 * Math.sin(t * 3 + e.id));
        const g = (t * 0.7 + e.id) % 1;
        if (g < 0.18) this.glow.add(p.x + Math.sin(t * 5) * 0.15, p.y + top * 0.85, p.z + Math.cos(t * 5) * 0.15, PALE, 0.5, 0.6 * (1 - g / 0.18));
      }
      else if (Math.random() < 0.25) this.strips(new THREE.Vector3(p.x, p.y + top * 0.7, p.z), 1, 0.4);
    }
    this.glow.end();
    void s;
  }

  /** Every frame: time, and the strips tumbling down. */
  frame(now: number): void {
    const dt = this.last ? Math.min(0.1, now - this.last) : 0;
    this.last = now;
    this.now = now;
    const steps = dt * 60;
    for (const p of this.paper) {
      p.life += steps;
      p.v.y -= 0.0012 * steps;
      p.v.multiplyScalar(Math.pow(0.97, steps));
      p.p.addScaledVector(p.v, steps);
      const floor = this.ctx.ground(p.p.x, p.p.z) + 0.02;
      if (p.p.y < floor) {
        p.p.y = floor;
        p.v.set(0, 0, 0);
      } else {
        p.r.x += p.w.x * steps;
        p.r.y += p.w.y * steps;
        p.r.z += p.w.z * steps;
      }
    }
    this.paper = this.paper.filter((p) => p.life < p.max);
    let n = 0;
    for (const p of this.paper) {
      const k = 1 - Math.max(0, (p.life - p.max * 0.6) / (p.max * 0.4));
      this.q.setFromEuler(p.r);
      this.m.compose(p.p, this.q, this.s3.set(k, k, k));
      this.papers.setMatrixAt(n++, this.m);
    }
    this.papers.count = n;
    this.papers.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const m of this.posts) m.geometry.dispose();
    this.papers.geometry.dispose();
    this.glow.mesh.geometry.dispose();
    this.tag?.material.map?.dispose();
    this.group.removeFromParent();
  }
}
